import { MissingPlaceHolder } from '@/app/features/reports/components/test-tile/missing-value';
import { Pill } from '@/components/raytd/pill';
import { RatingScore } from '@/components/raytd/rating-tool';
import isTestFieldRequired from '@app.raytd.com/common/utils/test-suites/getFieldRequirement';
import { cn } from '@/lib/utils';
import React, { memo, useCallback, useMemo } from 'react';
import { RatingTypes } from '../types';
import { ElementPath } from '../types/report';
import { ReportTestItem, TestSpecification } from '../utils/entities';
import { useReportDisplayContext, useReportViewContext } from './report-viewer-context';
import { useTestTileContext } from './test-tile/context';
import HighlightText from '@/app/features/reports/components/test-tile/highlight-text';
import { formatTestSuiteType } from '@/utils/formatTestSuiteType';

function MissingValue({ isMissing }: { isMissing: boolean }) {
    if (isMissing) {
        return <MissingPlaceHolder />;
    }
    return null;
}

function CompulsoryTile({ isMissing, className, children }: { isMissing: boolean, className?: string, children: React.ReactNode }) {
    const { showMissingData } = useTestTileContext();

    if (isMissing && showMissingData) {
        return <MissingPlaceHolder className={className} />;
    }
    return children;
}


const HeaderTile: React.FC<{
    title: string;
    subtitle: string;
    className?: string;
    isMissing?: boolean;
}> = ({ title, subtitle, className, isMissing }) => {

    if (isMissing) {
        return (
            <div className={cn(className, "space-y-0.5")}>
                <MissingPlaceHolder />
                <MissingPlaceHolder />
            </div>
        )
    }

    return (
        <div className={cn(className, "")}>
            <div className="text-xs text-zinc-950"><HighlightText>{subtitle}</HighlightText></div>
            <div className="text-zinc-950 text-base font-medium"><HighlightText>{title}</HighlightText></div>
        </div>
    )
}

interface HeaderSlotProps {
    left?: React.ReactNode;
    right?: React.ReactNode;
    bottom?: React.ReactNode;
    bottomRight?: React.ReactNode;
    ratingTool?: React.ReactNode;
}

const HeaderLayout: React.FC<HeaderSlotProps> = ({ left, right, bottom, bottomRight, ratingTool }) => {
    const { headerMode } = useReportDisplayContext();

    const slot1 = headerMode === 'normal' ? left : right;
    const slot2 = headerMode === 'normal' ? right : left;

    return (
        <div className="min-h-[60px] flex-shrink flex flex-col pl-4 pr-2 h-auto mb-2 pt-2">
            <div className="flex-1 flex w-full gap-4 justify-start items-start flex-row flex-wrap md:flex-nowrap">

                <div className="flex-1 items-center flex">{slot1}</div>

                <div className="flex-1 flex justify-between items-start">
                    <div className="flex-1 flex flex-row flex-wrap md:flex-nowrap items-start justify-start gap-4">
                        {slot2}
                        <div className="w-[180px] md:w-[180px] flex flex-shrink-0 items-start justify-start md:justify-end print:hidden">
                            {ratingTool}
                        </div>
                    </div>
                </div>
            </div>
            {(bottom || bottomRight) && (
                <div className="flex flex-row justify-between mt-3">
                    {bottom && <div className="flex flex-1">{bottom}</div>}
                    {bottomRight && <div className="flex justify-end">{bottomRight}</div>}
                </div>
            )}
        </div>
    );
};

const HeaderLayoutPrint: React.FC<HeaderSlotProps> = ({ left, right, bottom, bottomRight, ratingTool }) => {
    const { headerMode } = useReportDisplayContext();

    const slot1 = headerMode === 'normal' ? left : right;
    const slot2 = headerMode === 'normal' ? right : left;

    return (
        <div>
            <div className="flex flex-shrink-0 items-end justify-between mb-6 pl-2 pr-2">
            <div className="flex flex-row items-center gap-4">
                    {bottomRight && <div className="flex justify-end">{bottomRight}</div>}
                </div>
                {ratingTool}
            </div>

            <div className="min-h-[60px] flex-shrink flex flex-col pl-2 pr-2 h-auto mb-2 pt-2">
                <div className="flex-1 flex w-full gap-4 justify-start items-start flex-row flex-wrap md:flex-nowrap">

                    <div className="flex-1 items-center flex">{slot1}</div>

                    <div className="flex-1 flex justify-between items-start">
                        <div className="flex-1 flex flex-row flex-wrap md:flex-nowrap items-start justify-start gap-4">
                            {slot2}

                        </div>
                    </div>
                </div>

                <div className="flex flex-shrink-0 items-end justify-between py-4">
                <div className="flex flex-row items-center gap-4">
                    {bottom && <div className="flex flex-1">{bottom}</div>}
                </div>


            </div>
            </div>
        </div>
    );
};

interface TestItemHeaderProps {
    item: ReportTestItem;
}

interface LocationInfoProps {
    areaDescription: string;
    assetName: string;
    buildingDescription: string;
    levelDescription: string;
}

interface TestLineProps {
    children: React.ReactNode;
    variant: 'title' | 'subtitle';
}

const TestLineVariants = {
    title: 'text-zinc-950 text-sm print:text-base font-medium',
    subtitle: 'text-xs print:text-base text-zinc-950',
}

function TextLine({ children, variant }: TestLineProps) {

    return (
        <div className={cn(TestLineVariants[variant])}><HighlightText>{children}</HighlightText></div>
    )
}
type LocationLevel = 'building' | 'level' | 'area';

function getFinalLevel(
    building?: string,
    level?: string,
    area?: string
): LocationLevel | null {
    if (area) return 'area';
    if (level) return 'level';
    if (building) return 'building';
    return null;
}


export const LocationInfo = memo((
    {
        areaDescription,
        assetName,
        buildingDescription,
        levelDescription
    }: LocationInfoProps
) => {
    const { missingData, showMissingData, fieldSettings } = useTestTileContext();

    // Memoize the 'missing' calculation. It will only re-run if `missingData` changes.
    const missing = useMemo(() =>
        !!(missingData?.building_id || missingData?.level_id || missingData?.area_id),
        [missingData]
    );

    // Memoize the 'finalLevel' calculation. It only re-runs if location descriptions change.
    const finalLevel = useMemo(() =>
        getFinalLevel(buildingDescription, levelDescription, areaDescription),
        [buildingDescription, levelDescription, areaDescription]
    );

    // Create a data structure for rendering. This is more scalable and DRY.
    // Memoize this array so it's not recreated on every render.
    const locationHierarchy = useMemo(() => [
        { type: 'building', description: buildingDescription },
        { type: 'level', description: levelDescription },
        { type: 'area', description: areaDescription },
    ].filter((item): item is { type: LocationLevel; description: string } =>
        Boolean(item.description)
    ), [buildingDescription, levelDescription, areaDescription]);

    const shouldShowLocation = fieldSettings?.location?.active;

    return (
        <div className={cn("flex-1 space-y-0.5")}>
            <TextLine variant="subtitle">{assetName}</TextLine>

            {/* Conditional rendering for the location block */}
            {shouldShowLocation && !missing && (
                locationHierarchy.map(item => (
                    <TextLine
                        key={item.type}
                        variant={finalLevel === item.type ? 'title' : 'subtitle'}
                    >
                        {item.description}
                    </TextLine>
                ))
            )}

            {/* Conditional rendering for the missing data placeholder */}
            {shouldShowLocation && missing && (
                showMissingData ? (
                    <MissingPlaceHolder />
                ) : (
                    <TextLine variant="title">(No Location)</TextLine>
                ))}
        </div>
    );
});

function EmptyLocationInfo() {
    return (
        <div className={cn("flex-1", "space-y-0.5")}>
            &nbsp;
        </div>
    );
}


interface ElementInfoProps {
    elementPath: ElementPath[];
}

export const ElementInfo: React.FC<ElementInfoProps> = memo(({
    elementPath,
}) => {
    const { missingData, showMissingData, fieldSettings } = useTestTileContext();

    const isMissing = !!(missingData?.['element']);
    const shouldShowElement = fieldSettings?.element?.active;

    if (!shouldShowElement) {
        return <EmptyLocationInfo />
    }

    const finalElement = elementPath?.[elementPath.length - 1];

    return (
        <div className={cn("flex-1", "space-y-0.5")}>
            {isMissing && showMissingData ? (
                <MissingPlaceHolder />
            ) : (
                <>
                    {elementPath && elementPath.length > 0 ? (
                        elementPath.map((element, index) => (
                            <TextLine
                                key={`element-${element.id}-${index}`}
                                variant={finalElement?.id === element.id ? 'title' : 'subtitle'}
                            >
                                {element.name}
                            </TextLine>
                        ))
                    ) : (
                        <TextLine variant="title">(No Element)</TextLine>
                    )}
                </>
            )}
        </div>
    );
});

interface TestInfoProps {
    testSpecification: TestSpecification;
}

export const TestInfo = memo((
    {
        testSpecification
    }: TestInfoProps
) => {

    const testHierarchy = [
        testSpecification.test_suite_title,
        ...(testSpecification.type.includes('compliance')
            ? [testSpecification.category, testSpecification.subcategory]
            : []),
    ].filter(Boolean);


    const title = testSpecification.type === 'condition' ? formatTestSuiteType('condition') + ' Test'
        : testSpecification.type === 'generic' ? formatTestSuiteType('generic') + ' Test'
            : testSpecification.label;

    return (
        <div className={cn("flex-1", "space-y-0.5")}>
            {testHierarchy.map((item, index) => (
                <TextLine key={index} variant="subtitle">
                    {item}
                </TextLine>
            ))}
            <TextLine variant="title">{title || '(No Test)'}</TextLine>
        </div>
    );
});

interface ElementPillsProps {
    elementId?: string;
    elementName?: string;
}

export const ElementPills = memo(({
    elementId,
    elementName
}: ElementPillsProps) => {

    const { missingData, showMissingData, fieldSettings } = useTestTileContext();
    const elementIdMissing = !!(missingData?.['element_id']);
    const elementNameMissing = !!(missingData?.['element_name']);

    const shouldShowElementId = fieldSettings?.element_id?.active;
    const shouldShowElementName = fieldSettings?.element_name?.active;

    return (
        <div className="flex flex-shrink flex-row gap-4 items-end">
            {shouldShowElementId && (
                <CompulsoryTile isMissing={elementIdMissing} className="w-12 h-6">
                    {elementId && (
                        <Pill variant="disabled" size="sm" className="h-6">
                            <HighlightText>{elementId}</HighlightText>
                        </Pill>
                    )}
                </CompulsoryTile>
            )}

            {shouldShowElementName && (
                <CompulsoryTile isMissing={elementNameMissing} className="w-12 h-6">
                    {elementName && (
                        <Pill variant="disabled" size="sm" className="h-6">
                            <HighlightText>{elementName}</HighlightText>
                        </Pill>
                    )}
                </CompulsoryTile>
            )}
        </div>
    )
});

function TestID({ item }: { item: ReportTestItem }) {

    return (
        <Pill variant="disabled" size="sm" className="h-6">
            <HighlightText>Test {item.assessmentTestId}</HighlightText>
        </Pill>
    )
}

export const TestItemHeader = memo(({ item }: TestItemHeaderProps) => {

    const { groupMode } = useReportViewContext();
    const { missingData, showMissingData } = useTestTileContext();
    const { testSuite } = useTestTileContext();

    const isPrintView = useReportViewContext().viewId === 'print';

    if (!item) {
        return null;
    }

    const elementPath = item?.element?.path || [];

    const HeaderComponent = isPrintView ? HeaderLayoutPrint : HeaderLayout;

    const isNotApplicable =
        !item.result &&
        !!testSuite &&
        !isTestFieldRequired(testSuite, 'rating') &&
        item.testSpecification?.type === 'compliance';

    return (
        <HeaderComponent
            left={
                groupMode === 'location' ? (
                    <ElementInfo elementPath={elementPath} />
                ) : (
                    <LocationInfo
                        areaDescription={item?.area_description}
                        assetName={item?.asset_name}
                        buildingDescription={item?.building_description}
                        levelDescription={item?.level_description}
                    />
                )
            }
            right={
                groupMode == 'location' || groupMode === 'element' ? (
                    <TestInfo
                        testSpecification={item?.testSpecification}
                    />
                ) : (
                    <ElementInfo elementPath={elementPath} />
                )
            }
            bottom={
                <ElementPills
                    elementId={item?.element_id}
                    elementName={item?.element_name}
                />
            }
            bottomRight={
                <TestID item={item} />
            }
            ratingTool={
                <RatingScore
                    type={item?.ratingType as RatingTypes}
                    width={210}
                    height={45}
                    score={item?.score}
                    showSelector={!isNotApplicable}
                    isMissing={!!missingData?.['result'] && showMissingData}
                    isNotApplicable={isNotApplicable}
                />
            }
        />
    );
});

// Add display names for better debugging
LocationInfo.displayName = 'LocationInfo';
TestInfo.displayName = 'TestInfo';
ElementPills.displayName = 'ElementPills';
TestItemHeader.displayName = 'TestItemHeader';

export default TestItemHeader;